import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

export type ClassProgressStatus = 'not_started' | 'started' | 'completed';

export type ClassProgressRow = {
  status: ClassProgressStatus;
  started_at: string | null;
  completed_at: string | null;
};

export function useClassProgress(classId?: string) {
  const [status, setStatus] = useState<ClassProgressStatus>('not_started');
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!mounted) return;
      const uid = userData.user?.id ?? null;
      setUserId(uid);
    })();
    return () => { mounted = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (!classId || !userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_class_progress')
        .select('status, started_at, completed_at')
        .eq('user_id', userId)
        .eq('class_id', classId)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      if (data) {
        setStatus(data.status as ClassProgressStatus);
      } else {
        setStatus('not_started');
      }
    } catch (e) {
      console.warn('Progress fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [classId, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const ensureStarted = useCallback(async () => {
    if (!classId || !userId) return;
    setUpdating(true);
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('user_class_progress')
        .upsert({ user_id: userId, class_id: classId, status: 'started', started_at: now }, { onConflict: 'user_id,class_id' });
      if (error) throw error;
      setStatus('started');
    } catch (e) {
      console.error('Failed to set started:', e);
    } finally {
      setUpdating(false);
    }
  }, [classId, userId]);

  const markCompleted = useCallback(async () => {
    if (!classId || !userId) return;
    setUpdating(true);
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('user_class_progress')
        .update({ status: 'completed', completed_at: now })
        .eq('user_id', userId)
        .eq('class_id', classId);
      if (error) throw error;
      setStatus('completed');
    } catch (e) {
      console.error('Failed to mark completed:', e);
    } finally {
      setUpdating(false);
    }
  }, [classId, userId]);

  const markNotStarted = useCallback(async () => {
    if (!classId || !userId) return;
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('user_class_progress')
        .update({ status: 'not_started', started_at: null, completed_at: null })
        .eq('user_id', userId)
        .eq('class_id', classId);
      if (error) throw error;
      setStatus('not_started');
    } catch (e) {
      console.error('Failed to set not started:', e);
    } finally {
      setUpdating(false);
    }
  }, [classId, userId]);

  const markStarted = useCallback(async () => {
    if (!classId || !userId) return;
    setUpdating(true);
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('user_class_progress')
        .update({ status: 'started', started_at: now, completed_at: null })
        .eq('user_id', userId)
        .eq('class_id', classId);
      if (error) throw error;
      setStatus('started');
    } catch (e) {
      console.error('Failed to set started:', e);
    } finally {
      setUpdating(false);
    }
  }, [classId, userId]);

  return useMemo(() => ({
    status,
    loading,
    updating,
    refresh,
    ensureStarted,
    markCompleted,
    markNotStarted,
    markStarted,
  }), [status, loading, updating, refresh, ensureStarted, markCompleted, markNotStarted, markStarted]);
}
